import { defineStore } from 'pinia'

export const useAudioPlayerStore = defineStore('audioPlayer', {
  state: () => ({
    currentUrl: null,
    isPlaying: false,
    progress: 0, // 0..1 ratio
    currentTimeSec: 0,
    timeByUrl: {}, // seconds per URL
    _audio: null,
    _durationByUrl: {},
  }),
  getters: {
    isPlayingUrl: (state) => (url) => state.isPlaying && state.currentUrl === url,
  },
  actions: {
    _ensureAudio() {
      if (!this._audio) {
        const audio = new Audio()
        audio.preload = 'metadata'
        audio.addEventListener('loadedmetadata', () => {
          // Apply any planned seek position (set during play())
          if (typeof this._plannedSeconds === 'number' && Number.isFinite(this._plannedSeconds) && this._plannedSeconds >= 0) {
            try {
              audio.currentTime = this._plannedSeconds
            } catch (e) {
              // ignore
            }
            // Clear once applied
            this._plannedSeconds = undefined
          }
        })
        audio.addEventListener('ended', () => {
          // On ended, force UI to 100% using the metadata duration provided by the caller
          this.isPlaying = false
          this.progress = 1
          let dur = 0
          if (this.currentUrl) {
            const metaDur = this._durationByUrl[this.currentUrl]
            if (Number.isFinite(metaDur) && metaDur > 0) {
              dur = metaDur
            }
            this.currentTimeSec = dur
            this.timeByUrl[this.currentUrl] = dur
          } else {
            this.currentTimeSec = dur
          }
        })
        audio.addEventListener('pause', () => {
          this.isPlaying = false
          if (!this.currentUrl) {
            return
          }
          if (audio.ended) {
            const metaDur = this._durationByUrl[this.currentUrl]
            const endTime = Number.isFinite(metaDur) && metaDur > 0 ? metaDur : 0
            this.timeByUrl[this.currentUrl] = endTime
            this.currentTimeSec = endTime
            this.progress = 1
            return
          }
          let t = 0
          if (Number.isFinite(audio.currentTime) && audio.currentTime >= 0) {
            t = audio.currentTime
          }
          this.timeByUrl[this.currentUrl] = t
          this.currentTimeSec = t
        })
        audio.addEventListener('play', () => {
          this.isPlaying = true
        })
        this._audio = audio
      }
    },
    getAudioEl() {
      this._ensureAudio()
      return this._audio
    },
    setTimeForUrl(url, seconds) {
      this.timeByUrl[url] = seconds
    },
    async play(url, seconds, totalDuration) {
      this._ensureAudio()

      // Toggle when same URL
      if (this.currentUrl === url) {
        if (Number.isFinite(totalDuration) && totalDuration > 0) {
          this._durationByUrl[url] = totalDuration
        }
        // If a start position is provided, seek there first
        if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0) {
          try {
            this._audio.currentTime = seconds
            this.currentTimeSec = seconds
          } catch (e) {
            // ignore
            // eslint-disable-next-line no-console
            console.warn('Seeking failed', e)
          }
        }
        if (this.isPlaying) {
          this._audio.pause()
          return
        }
        try {
          await this._audio.play()
          return
        } catch (e) {
          // ignore
          // eslint-disable-next-line no-console
          console.warn('Audio playback failed', e)
        }
        return
      }

      // Switch to a new URL
      try {
        this._audio.pause()
      } catch (e) {
        // ignore pause error when switching sources
      }
      // Persist current time for the previous URL before switching
      if (this.currentUrl) {
        this.timeByUrl[this.currentUrl] = Number.isFinite(this._audio.currentTime) ? this._audio.currentTime : 0
      }
      this.progress = 0
      this.currentTimeSec = 0
      this.currentUrl = url
      if (Number.isFinite(totalDuration) && totalDuration > 0) {
        this._durationByUrl[url] = totalDuration
      }
      // Plan to start at the provided seconds, or fall back to last known time for this URL
      if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0) {
        this._plannedSeconds = seconds
      } else {
        this._plannedSeconds = this.timeByUrl[url] || 0
      }
      this._audio.src = url
      try {
        await this._audio.play()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('Audio playback failed for new source', e)
      }
    },
    pause() {
      if (!this._audio) {
        return
      }
      this._audio.pause()
    },
    stop() {
      if (!this._audio) {
        return
      }
      this._audio.pause()
      try {
        this._audio.currentTime = 0
      } catch (e) {
        // ignore resetting time errors
        // eslint-disable-next-line no-console
        console.warn('Resetting currentTime failed', e)
      }
      this.progress = 0
      this.isPlaying = false
      this.currentTimeSec = 0
    },
    seekToSeconds(seconds) {
      this._ensureAudio()
      const sec = Number(seconds)
      if (!Number.isFinite(sec) || sec < 0) {
        // eslint-disable-next-line no-console
        console.warn(
          'AudioStore.seekToSeconds aborted (non-finite)',
          {
            seconds,
          },
        )
        return
      }
      try {
        this._audio.currentTime = sec
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('AudioStore.seekToSeconds failed to set currentTime', e)
        return
      }
      const duration = this._audio?.duration
      if (Number.isFinite(duration) && duration > 0) {
        this.progress = Math.min(1, Math.max(0, sec / duration))
      }
      this.currentTimeSec = sec
    },
  },
})
